// frontend/src/components/AdvancedFilterModal.jsx
import { useEffect, useState } from "react";
import { fetchCompanyFilters } from "../api.js";

const SERVICES = ["Private Banking", "Wealth Mgmt", "Custody", "Payments", "Other"];
const TX_TYPES = [
  { value: "payments", label: "Payments" },
  { value: "cheques", label: "Cheques" },
  { value: "securities", label: "Securities" },
  { value: "crypto_related", label: "Crypto related" },
  { value: "other", label: "Other" },
];

export default function AdvancedFilterModal({ open, onClose, filters, onApply }) {
  const [local, setLocal] = useState(filters || {});
  const [options, setOptions] = useState({});

  useEffect(() => {
    if (open) {
      setLocal(filters || {});
      fetchCompanyFilters()
        .then((data) => setOptions(data || {}))
        .catch(() => setOptions({}));
    }
  }, [open, filters]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const toggle = (key, val) => {
    setLocal((prev) => {
      const set = new Set(prev[key] || []);
      set.has(val) ? set.delete(val) : set.add(val);
      return { ...prev, [key]: [...set] };
    });
  };

  const handleApply = () => {
    onApply(local);
    onClose();
  };

  const handleReset = () => {
    setLocal({});
    onApply({});
    onClose();
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        zIndex: 1000,
        display: "grid",
        placeItems: "center",
        padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(900px, 100%)",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--surface)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-primary)",
          borderRadius: 12,
          padding: 28,
          boxShadow: "0 25px 60px rgba(0, 0, 0, 0.35)",
        }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Advanced Filters</h2>
          <button
            onClick={onClose}
            type="button"
            style={{
              width: 32,
              height: 32,
              display: "grid",
              placeItems: "center",
              borderRadius: 999,
              border: "1px solid var(--border-primary)",
              background: "var(--surface)",
              color: "var(--text-secondary)",
              cursor: "pointer",
              fontSize: 18,
            }}>
            ✕
          </button>
        </div>

        {/* Company Attributes */}
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-secondary)", marginBottom: 16 }}>
            Company Attributes
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Status</label>
              <select
                value={local.status?.[0] || ""}
                onChange={(e) => setLocal((p) => ({ ...p, status: e.target.value ? [e.target.value] : [] }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}>
                <option value="">— Any —</option>
                {(options.status || ["active", "pending_gov_approval", "liquidated", "rejected"]).map((s) => (
                  <option key={s} value={s}>
                    {s === "pending_gov_approval" ? "Pending" : s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Risk Level</label>
              <select
                multiple
                value={local.risk_level || []}
                onChange={(e) => setLocal((p) => ({ ...p, risk_level: Array.from(e.target.selectedOptions, (o) => o.value) }))}
                style={{
                  width: "100%",
                  minHeight: 100,
                  padding: 8,
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}>
                {["low", "medium", "high", "very_high"].map((r) => (
                  <option key={r} value={r}>
                    {r === "very_high" ? "Very High" : r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Residency</label>
              <select
                value={local.residency_status?.[0] || ""}
                onChange={(e) => setLocal((p) => ({ ...p, residency_status: e.target.value ? [e.target.value] : [] }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}>
                <option value="">— Any —</option>
                {["local", "international", "foreign"].map((r) => (
                  <option key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Legal Form</label>
              <select
                value={local.legal_form?.[0] || ""}
                onChange={(e) => setLocal((p) => ({ ...p, legal_form: e.target.value ? [e.target.value] : [] }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}>
                <option value="">— Any —</option>
                {(options.legal_form || []).map((lf) => (
                  <option key={lf.value || lf} value={lf.value || lf}>
                    {lf.label || lf}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Managed by</label>
              <select
                value={local.managed_by || ""}
                onChange={(e) => setLocal((p) => ({ ...p, managed_by: e.target.value }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}>
                <option value="">— Any —</option>
                <option value="gtrust">G-Trust Curaçao B.V.</option>
                <option value="prostaff">ProStaff Curacao N.V.</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Country</label>
              <input
                type="text"
                placeholder="Country name or ISO2"
                value={local.country || ""}
                onChange={(e) => setLocal((p) => ({ ...p, country: e.target.value }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}
              />
            </div>
          </div>
        </section>

        {/* Services & Activity */}
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-secondary)", marginBottom: 16 }}>
            Services & Activity
          </h3>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--text-secondary)" }}>Services</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {SERVICES.map((s) => {
                const checked = (local.services || []).includes(s);
                return (
                  <label
                    key={s}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 14px",
                      borderRadius: 999,
                      border: `1px solid ${checked ? "var(--accent-primary)" : "var(--border-primary)"}`,
                      background: checked ? "rgba(59, 130, 246, 0.15)" : "var(--surface)",
                      color: checked ? "var(--accent-primary)" : "var(--text-primary)",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                    }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle("services", s)} style={{ margin: 0 }} />
                    <span>{s}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--text-secondary)" }}>Transaction Types</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {TX_TYPES.map((tx) => {
                const checked = (local.transaction_types || []).includes(tx.value);
                return (
                  <label
                    key={tx.value}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 14px",
                      borderRadius: 999,
                      border: `1px solid ${checked ? "var(--accent-primary)" : "var(--border-primary)"}`,
                      background: checked ? "rgba(59, 130, 246, 0.15)" : "var(--surface)",
                      color: checked ? "var(--accent-primary)" : "var(--text-primary)",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                    }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle("transaction_types", tx.value)} style={{ margin: 0 }} />
                    <span>{tx.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Business Activity</label>
            <input
              type="text"
              placeholder="e.g., Protection of Assets"
              value={local.business_activity || ""}
              onChange={(e) => setLocal((p) => ({ ...p, business_activity: e.target.value }))}
              style={{
                width: "100%",
                height: 40,
                padding: "0 12px",
                borderRadius: 8,
                border: "1px solid var(--border-primary)",
                background: "var(--surface)",
                color: "var(--text-primary)",
              }}
            />
          </div>
        </section>

        {/* Dates & Documents */}
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-secondary)", marginBottom: 16 }}>Dates & Documents</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Relationship From</label>
              <input
                type="date"
                value={local.started_from || ""}
                onChange={(e) => setLocal((p) => ({ ...p, started_from: e.target.value }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Relationship To</label>
              <input
                type="date"
                value={local.started_to || ""}
                onChange={(e) => setLocal((p) => ({ ...p, started_to: e.target.value }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Has Bank Account</label>
              <select
                value={local.bank_account ?? ""}
                onChange={(e) => setLocal((p) => ({ ...p, bank_account: e.target.value === "" ? "" : e.target.value }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}>
                <option value="">— Either —</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>Has Documents</label>
              <select
                value={local.has_documents ?? ""}
                onChange={(e) => setLocal((p) => ({ ...p, has_documents: e.target.value === "" ? "" : e.target.value }))}
                style={{
                  width: "100%",
                  height: 40,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid var(--border-primary)",
                  background: "var(--surface)",
                  color: "var(--text-primary)",
                }}>
                <option value="">— Either —</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 16, borderTop: "1px solid var(--border-primary)" }}>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "1px solid var(--border-primary)",
              background: "var(--surface)",
              color: "var(--text-secondary)",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}>
            Clear All
          </button>
          <button
            type="button"
            onClick={handleApply}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "var(--accent-primary)",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}>
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}
